import React, { useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import Styles from './Version.module.css'
import { Card, CardBody, Box, Grid, GridItem } from '@chakra-ui/react'
import Header from '../Header/Header'
import Select from 'react-select'
import Features from '../Features/Features'
import Prerequisites from '../Prerequisites/Prerequisites'
import { Spinner } from '@chakra-ui/react'
import Connectors from '../Connectors/Connectors'
import TermsandConditions from './TermsandConditions/TermsandConditions'
import {
    useGetConnectorVersionsQuery,
    useLazyGetConnectorFeaturesQuery,
    useLazyGetConnectorQuestionsQuery,
    useLazyGetLatestConnectorVersionIdQuery
} from '../../../../features/marketplace/marketplaceApi'

const EMPTY = []

const uniqueOptions = (values) => [...new Set(values)].map((value) => ({ value, label: value }))

/** Trial sign-up, step 1: pick versions, review features, then answer prerequisites. */
const Versions = () => {
    const { id: connectorId, name: connectorName } = useParams()
    const location = useLocation()
    const { imageContent, index, sourceShortName, targetShortName } = location.state || {}

    const { data: versions = EMPTY, isLoading: isLoadingVersions } = useGetConnectorVersionsQuery(connectorId)
    const [getFeatures, { isFetching: isLoadingFeatures }] = useLazyGetConnectorFeaturesQuery()
    const [getLatestVersionId, { isFetching: isLoadingVersionId }] = useLazyGetLatestConnectorVersionIdQuery()
    const [getQuestions, { isFetching: isLoadingQuestions }] = useLazyGetConnectorQuestionsQuery()

    const [selectedSourceVersion, setSelectedSourceVersion] = useState(null)
    const [selectedTargetVersion, setSelectedTargetVersion] = useState(null)
    const [versionEntities, setVersionEntities] = useState(null)
    const [questions, setQuestions] = useState(EMPTY)
    const [connectorVersionId, setConnectorVersionId] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [next, setNext] = useState(0)

    const loading = isLoadingVersions || isLoadingFeatures || isLoadingVersionId || isLoadingQuestions
    const sourceVersionOptions = uniqueOptions(versions.map((item) => item.sourceVersion))
    const targetVersionOptions = uniqueOptions(versions.map((item) => item.targetVersion))

    const confirm = async () => {
        if (selectedSourceVersion === null || selectedTargetVersion === null) {
            setErrorMessage('Please select Source and Target versions')
            return
        }
        setNext(1)
        setErrorMessage(null)
        try {
            const features = await getFeatures({
                connectorId,
                sourceVersion: selectedSourceVersion.value,
                targetVersion: selectedTargetVersion.value
            }).unwrap()
            const latest = features[0] && features[0].latestVersion
            setVersionEntities(latest ? latest.versionEntities : null)
        } catch (error) {
            // The features panel stays empty; the version and questions below still load.
        }
        try {
            const versionId = await getLatestVersionId(connectorId).unwrap()
            setConnectorVersionId(versionId)
            setQuestions(await getQuestions(versionId).unwrap())
        } catch (error) {
            setErrorMessage('The prerequisites could not be loaded. Please try again.')
        }
    }

    const versionError = () => setErrorMessage('Please select Source and Target versions')

    return (
        <>
            {
                loading && loading ? 
                <Box className={Styles.parentBoxofLoader}>
                    <Box w={'100%'} height={'100%'} position={'relative'}>
                    <Spinner
                        thickness='4px'
                        speed='0.65s'
                        emptyColor='gray.200'
                        color='blue.500'
                        size='xl'
                        position={'relative'}
                        top={'50%'}
                        transform={'translateY(-50%)'}
                        left={'50%'}
                    />
                    </Box>
                </Box>
                : null
            }
            <Header />
            {
                loading && loading ? 
                <Box className={'parentBoxofLoader'}>
                    <Box w={'100%'} height={'100%'} position={'relative'}>
                    <Box className={'spinnerLanding'}></Box>
                    </Box>
                </Box>
                : null
            }
            <Box className={Styles.mnContainer} mb={'15px'}>
                <Grid
                    templateAreas={`"nav main" "nav footer"`}
                    gridTemplateRows={'50px 1fr'}
                    gridTemplateColumns={'350px 1fr'}
                    h='auto'
                    gap='20px'
                    color='blackAlpha.700'
                    fontWeight='bold'
                >
                    <GridItem area={'nav'} border={'1px solid #e2e8f0'} className={Styles.Grid}>
                        <Card maxW={'350px'} borderBottom={'1px solid #e2e8f0'}>
                            <CardBody>
                                <Connectors 
                                    index={index}
                                    name={connectorName}
                                    image={imageContent}
                                    sourceShortName={sourceShortName}
                                    targetShortName={targetShortName}
                                />
                            </CardBody>
                        </Card>
                        <Card maxW={'350px'} mt={'20px'} className='versionBox'>
                            <CardBody>
                                <Box style={{ marginBottom: '20px' }}>
                                    <label>Source Version</label>
                                    <Select
                                        options={sourceVersionOptions}
                                        value={selectedSourceVersion}
                                        onChange={setSelectedSourceVersion}
                                        placeholder="Select Source Version"
                                    />
                                </Box>
                                <Box style={{ marginBottom: '20px' }}>
                                    <label>Target Version</label>
                                    <Select
                                        options={targetVersionOptions}
                                        value={selectedTargetVersion}
                                        onChange={setSelectedTargetVersion}
                                        placeholder="Select Target Version"
                                    />
                                </Box>
                                {
                                    (selectedSourceVersion !== null) && (selectedTargetVersion !== null)?
                                        <TermsandConditions confirm={confirm} buttonStatus={false} />
                                    : null
                                }
                            </CardBody>
                        </Card>
                    </GridItem>
                    <GridItem area={'main'} border={'1px solid #e2e8f0'}>
                        <Card>
                            <CardBody>
                                <Box overflowY={'auto'}>
                                    <Features
                                        connectorName={connectorName}
                                        versionEntities={versionEntities}
                                    />
                                    {
                                        errorMessage && errorMessage !== null ? <Box className={Styles.warningMessage}> {errorMessage && errorMessage} </Box> : null
                                    }
                                </Box>
                                {
                                    next === 1 ? 
                                        <Prerequisites
                                            questions = {questions && questions}
                                            sourceVersion= {selectedSourceVersion && selectedSourceVersion.value}
                                            targetVersion= {selectedTargetVersion && selectedTargetVersion.value}
                                            versionError={versionError}
                                            connectorVersionId={connectorVersionId}
                                        />
                                    :null
                                }
                            </CardBody>
                        </Card>
                    </GridItem>
                </Grid>
            </Box>
        </>
    );
};

export default Versions;
